import React from "react";
import { Link as InertiaLink, router } from "@inertiajs/react";
import {
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Button,
  Chip,
  Input,
  Select,
  SelectItem,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Divider,
} from "@heroui/react";
import Layout from "../Layouts/Layout";

export default function Index({ tasks, can_create, filters, stats }) {
  const [searchQuery, setSearchQuery] = React.useState(filters?.search || "");
  const [sortBy, setSortBy] = React.useState(filters?.sort_by || "recent");
  const [filterVisibility, setFilterVisibility] = React.useState(filters?.visibility || "all");
  const [viewMode, setViewMode] = React.useState("grid");

  // Update URL parameters when filters change
  const updateFilters = React.useCallback((newFilters) => {
    router.get('/tasks', newFilters, {
      preserveState: true,
      preserveScroll: true,
      only: ['tasks', 'filters']
    });
  }, []);

  // Handle search submit
  const handleSearch = React.useCallback(() => {
    updateFilters({
      search: searchQuery,
      sort_by: sortBy,
      visibility: filterVisibility
    });
  }, [searchQuery, sortBy, filterVisibility, updateFilters]);

  // Handle sort change
  const handleSortChange = React.useCallback((newSortBy) => {
    setSortBy(newSortBy);
    updateFilters({
      search: searchQuery,
      sort_by: newSortBy,
      visibility: filterVisibility
    });
  }, [searchQuery, filterVisibility, updateFilters]);

  // Handle visibility filter change
  const handleVisibilityChange = React.useCallback((newVisibility) => {
    setFilterVisibility(newVisibility);
    updateFilters({
      search: searchQuery,
      sort_by: sortBy,
      visibility: newVisibility
    });
  }, [searchQuery, sortBy, updateFilters]);

  return (
    <Layout>
      {/* Header */}
      <div className="flex flex-col gap-6 mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Tasks
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage and run your AI-powered workflows
            </p>
          </div>
          {can_create && (
            <Button
              as={InertiaLink}
              href="/tasks/new"
              className="bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40"
              size="lg"
              startContent={
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              }
            >
              Create task
            </Button>
          )}
        </div>

        {/* Stats Bar */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200/50 dark:border-gray-700/50">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total tasks
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.total}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200/50 dark:border-gray-700/50">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total runs
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.total_runs}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200/50 dark:border-gray-700/50">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Public
              </p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                {stats.public_count}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200/50 dark:border-gray-700/50">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Private
              </p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
                {stats.private_count}
              </p>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        {(tasks.length > 0 || searchQuery || filterVisibility !== "all") && (
          <div className="flex flex-col sm:flex-row gap-4">
            <Input
              placeholder="Search tasks..."
              value={searchQuery}
              onValueChange={setSearchQuery}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              startContent={
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              }
              endContent={
                searchQuery && (
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    onPress={handleSearch}
                    className="min-w-unit-6 w-6 h-6"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </Button>
                )
              }
              classNames={{
                base: "max-w-xs",
                inputWrapper:
                  "bg-white dark:bg-gray-800/50 border border-gray-200/50 dark:border-gray-700/50",
              }}
              isClearable
              onClear={() => {
                setSearchQuery("");
                updateFilters({
                  search: "",
                  sort_by: sortBy,
                  visibility: filterVisibility
                });
              }}
            />

            <Select
              placeholder="Sort by"
              selectedKeys={[sortBy]}
              onSelectionChange={(keys) => handleSortChange(Array.from(keys)[0])}
              classNames={{
                base: "max-w-[150px]",
                trigger:
                  "bg-white dark:bg-gray-800/50 border border-gray-200/50 dark:border-gray-700/50",
              }}
            >
              <SelectItem key="recent">Recent</SelectItem>
              <SelectItem key="name">Name</SelectItem>
              <SelectItem key="runs">Most runs</SelectItem>
            </Select>

            <Select
              placeholder="Visibility"
              selectedKeys={[filterVisibility]}
              onSelectionChange={(keys) =>
                handleVisibilityChange(Array.from(keys)[0])
              }
              classNames={{
                base: "max-w-[150px]",
                trigger:
                  "bg-white dark:bg-gray-800/50 border border-gray-200/50 dark:border-gray-700/50",
              }}
            >
              <SelectItem key="all">All</SelectItem>
              <SelectItem key="public">Public</SelectItem>
              <SelectItem key="private">Private</SelectItem>
              <SelectItem key="incognito">Incognito</SelectItem>
            </Select>

            {(searchQuery || filterVisibility !== "all" || sortBy !== "recent") && (
              <Button
                variant="flat"
                color="default"
                size="md"
                onPress={() => {
                  setSearchQuery("");
                  setSortBy("recent");
                  setFilterVisibility("all");
                  updateFilters({
                    search: "",
                    sort_by: "recent",
                    visibility: "all"
                  });
                }}
                startContent={
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                }
              >
                Clear
              </Button>
            )}

            <div className="flex gap-1 ml-auto">
              <Button
                isIconOnly
                variant={viewMode === "grid" ? "flat" : "light"}
                onPress={() => setViewMode("grid")}
                className={
                  viewMode === "grid"
                    ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
                    : ""
                }
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                  />
                </svg>
              </Button>
              <Button
                isIconOnly
                variant={viewMode === "list" ? "flat" : "light"}
                onPress={() => setViewMode("list")}
                className={
                  viewMode === "list"
                    ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
                    : ""
                }
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      {tasks.length === 0 ? (
        <Card className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
          <CardBody className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-amber-600 dark:text-amber-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No tasks yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              Create your first AI task to automate workflows, generate content,
              analyze data, or build custom AI-powered tools.
            </p>
            {can_create && (
              <Button
                as={InertiaLink}
                href="/tasks/new"
                className="bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold"
                size="lg"
              >
                Create your first task
              </Button>
            )}
          </CardBody>
        </Card>
      ) : tasks.length === 0 && (searchQuery || filterVisibility !== "all") ? (
        <Card className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
          <CardBody className="text-center py-12">
            <svg
              className="w-12 h-12 mx-auto mb-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No results found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Try adjusting your search or filter criteria
            </p>
            <Button
              variant="light"
              color="warning"
              className="mt-4"
              onPress={() => {
                setSearchQuery("");
                setFilterVisibility("all");
                updateFilters({
                  search: "",
                  sort_by: sortBy,
                  visibility: "all"
                });
              }}
            >
              Clear filters
            </Button>
          </CardBody>
        </Card>
      ) : viewMode === "grid" ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {tasks.map((task) => (
            <Card
              key={task.id}
              isPressable
              onPress={() => router.visit(`/tasks/${task.id}`)}
              className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 hover:shadow-lg hover:shadow-gray-200/50 dark:hover:shadow-gray-900/50 transition-all group cursor-pointer"
            >
              <CardHeader className="flex justify-between items-start pb-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
                  {task.name}
                </h3>
                <Chip
                  color={
                    task.visibility === "public"
                      ? "success"
                      : task.visibility === "incognito"
                        ? "default"
                        : "primary"
                  }
                  variant="flat"
                  size="sm"
                >
                  {task.visibility}
                </Chip>
              </CardHeader>

              <CardBody className="pt-0 pb-2">
                {task.description ? (
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                    {task.description}
                  </p>
                ) : (
                  <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                    No description
                  </p>
                )}
              </CardBody>

              <Divider />

              <CardFooter className="flex justify-between items-center pt-3">
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  {task.user.name}
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    {task.run_count}
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <Dropdown>
                      <DropdownTrigger>
                        <Button
                          isIconOnly
                          variant="light"
                          size="sm"
                          className="text-gray-500"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                            />
                          </svg>
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu>
                        <DropdownItem
                          key="run"
                          as={InertiaLink}
                          href={`/tasks/${task.id}`}
                          startContent={
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          }
                        >
                          Run task
                        </DropdownItem>
                        <DropdownItem
                          key="edit"
                          as={InertiaLink}
                          href={`/tasks/${task.id}/edit`}
                          startContent={
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                          }
                        >
                          Edit
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </div>
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {tasks.map((task) => (
            <Card
              key={task.id}
              isPressable
              onPress={() => router.visit(`/tasks/${task.id}`)}
              className="w-full bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 hover:shadow-md transition-shadow cursor-pointer"
            >
              <CardBody className="flex flex-row items-center gap-4 py-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white hover:text-amber-600 dark:hover:text-amber-400 transition-colors truncate">
                      {task.name}
                    </h3>
                    <Chip
                      color={
                        task.visibility === "public"
                          ? "success"
                          : task.visibility === "incognito"
                            ? "default"
                            : "primary"
                      }
                      variant="flat"
                      size="sm"
                    >
                      {task.visibility}
                    </Chip>
                  </div>
                  {task.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                      {task.description}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-6 text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    {task.user.name}
                  </div>
                  <div className="flex items-center gap-1">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    {task.run_count} runs
                  </div>
                </div>

                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    as={InertiaLink}
                    href={`/tasks/${task.id}`}
                    size="sm"
                    variant="flat"
                    color="warning"
                  >
                    Run
                  </Button>
                  <Button
                    as={InertiaLink}
                    href={`/tasks/${task.id}/edit`}
                    size="sm"
                    variant="light"
                  >
                    Edit
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </Layout>
  );
}
